import type { FC, ReactNode } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { ThreadTimelineCursor } from "@productlane/zero/queries"
import { queries } from "@productlane/zero/queries"

import type { TimelinePageData } from "../lib/timeline-pagination"

const cursorKey = (cursor: ThreadTimelineCursor) =>
	`${cursor.createdAt}:${cursor.id}`

interface PageLoaderProps {
	threadId: string
	cursor: ThreadTimelineCursor
	onResult: (key: string, data: TimelinePageData) => void
}

// Renders nothing; exists so each fixed-cursor page gets its own useQuery
// (hooks can't run in a loop over a growing pages array).
const OlderMessagesPageLoader: FC<PageLoaderProps> = ({
	threadId,
	cursor,
	onResult,
}) => {
	const [row, queryResult] = useQuery(
		queries.threadOlderMessages({ threadId, before: cursor }),
		{ ttl: "10m" },
	)
	const complete = queryResult.type === "complete"
	const key = cursorKey(cursor)
	const data = useMemo<TimelinePageData>(
		() => ({ cursor, row: row ?? undefined, complete }),
		[cursor, row, complete],
	)
	useEffect(() => {
		onResult(key, data)
	}, [key, data, onResult])
	return null
}

// Loads one threadOlderMessages page per cursor and returns their results
// aligned to `pages` order. Mount `pagesNode` anywhere in the tree.
export function useTimelinePages(
	threadId: string,
	pages: ThreadTimelineCursor[],
): { pageData: TimelinePageData[]; pagesNode: ReactNode } {
	const [results, setResults] = useState<Record<string, TimelinePageData>>({})

	const handleResult = useCallback((key: string, data: TimelinePageData) => {
		setResults((prev) => (prev[key] === data ? prev : { ...prev, [key]: data }))
	}, [])

	const pageData = useMemo(
		() =>
			pages.map(
				(cursor) =>
					results[cursorKey(cursor)] ?? {
						cursor,
						row: undefined,
						complete: false,
					},
			),
		[pages, results],
	)

	const pagesNode = (
		<>
			{pages.map((cursor) => (
				<OlderMessagesPageLoader
					key={cursorKey(cursor)}
					threadId={threadId}
					cursor={cursor}
					onResult={handleResult}
				/>
			))}
		</>
	)

	return { pageData, pagesNode }
}
